/**
 * Workspace boards tab (FRIDAY-65).
 *
 * Lists the boards of the workspace and links to each board's settings. The
 * destructive action (Archive board) lives in the board's own settings
 * screen, where the board is named and the restore path is explained.
 */

import { Link } from '@tanstack/react-router'
import { useWorkspaceOverview } from '@/features/workspaces/workspaces'
import { Settings } from 'lucide-react'

export function WorkspaceBoardsPanel({ wsId }: { wsId: string }) {
  const overviewQ = useWorkspaceOverview(wsId)

  if (overviewQ.isLoading) {
    return (
      <p className="text-surface-500 p-4 text-sm" role="status">
        Loading boards…
      </p>
    )
  }

  if (overviewQ.isError) {
    return (
      <div className="p-4" role="alert">
        <p className="text-sm text-red-600 dark:text-red-300">Could not load the boards of this workspace.</p>
        <button type="button" className="btn-ghost mt-2" onClick={() => void overviewQ.refetch()}>
          Retry
        </button>
      </div>
    )
  }

  const boards = overviewQ.data?.projects ?? []

  if (boards.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-surface-200 p-12 text-center text-sm text-surface-500 dark:border-surface-700">
        No boards in this workspace yet.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-surface-500 text-xs">
        Every board keeps its own labels, milestones and columns. Open a board's settings to rename it, change
        its columns or archive it.
      </p>
      <ul className="space-y-2" aria-label="Boards">
        {boards.map((board) => (
          <li key={board.id} className="card p-3" data-testid="workspace-board">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-semibold text-white"
                style={{ backgroundColor: board.color }}
                aria-hidden="true"
              >
                {board.key.slice(0, 2)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="truncate text-sm font-semibold">{board.name}</span>
                  <span className="font-mono text-[10px] text-surface-500">{board.key}</span>
                </div>
                <div className="text-xs text-surface-500">
                  {board.taskCounts.open} open · {board.taskCounts.in_progress} in progress ·{' '}
                  {board.taskCounts.done} done
                </div>
              </div>
              <Link
                to="/w/$wsId/p/$pKey/settings"
                params={{ wsId, pKey: board.key }}
                className="btn-ghost min-h-11 text-xs"
              >
                <Settings size={14} aria-hidden="true" />
                Open settings
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
